import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { BsMessenger } from "react-icons/bs";
import {
  MdCampaign,
  MdChatBubbleOutline,
  MdErrorOutline,
  MdGroups,
  MdHandshake,
  MdHourglassEmpty,
  MdRoute,
  MdShoppingBag,
  MdStorefront,
} from "react-icons/md";
import { api } from "../api/client";
import type { AdPerson } from "../api/types";
import { adLabel } from "../customers";
import { adName } from "../ui";
import { Btn, Drawer } from "./Kit";
import "../journey.css";

/**
 * 广告足迹: one customer's road — how they first came, every ad they tapped as
 * its own picture, every order as a bag — drawn the same in the ads report's
 * 旧客户 list, the chat's 详情 and the 客户表 (owner, 2026-10-12: 「如果我想知道
 * 顾客点过什么广告我要到哪里看？…我要整齐 直观 有美观 有质感的 呈现方式 学习成本要低
 * 不要全是字的多用图和icon呈现」). A sentence a step read as a wall of words; the
 * pictures are the ads as the team knows them, from the chat's own ad card.
 *
 * Its own component, taking the road itself or a number, so the 客户档案 can set
 * it in its timeline as it is.
 */

/** How somebody first came, as an icon says it. */
type Came = "ad" | "adUnknown" | "self" | "messenger" | "web" | "dealer" | "referral" | "other";

export type JourneyStep =
  | { kind: "came"; at: number; how: Came; adID: string; ad: string; mine: boolean }
  | { kind: "tap"; at: number; to: number; times: number; adID: string; ad: string; mine: boolean }
  | { kind: "buy"; at: number; total: number; adID: string; ad: string; mine: boolean; after: boolean };

export type AdPics = Record<string, string>;

const when = (iso?: string) => {
  const t = Date.parse(iso || "");
  return Number.isNaN(t) || (iso ?? "").startsWith("0001") ? NaN : t;
};

/** 「9/23」 in Malaysia. */
export const dayOf = (t: number) => {
  if (Number.isNaN(t)) return "";
  const [, m = "", d = ""] = new Date(t).toLocaleDateString("en-CA", { timeZone: "Asia/Kuala_Lumpur" }).split("-");
  return `${+m}/${+d}`;
};

/** Whole ringgit without the sen: RM158, not RM158.00. */
export const rmOf = (n: number) =>
  `RM${n.toLocaleString("en-MY", { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;

const cameOf = (origin?: string): Came => {
  switch (origin) {
    case "ad":
      return "adUnknown";
    case "messenger":
    case "web":
    case "dealer":
    case "referral":
      return origin;
    case "pm":
    case "":
    case undefined:
      return "self";
  }
  return "other";
};

/**
 * A person's road, oldest first. Their first message, as the tap it came with
 * when there was one; each ad they tapped — the same one again with nothing
 * between is one step, ×N; each order. mine are the ads the road is drawn for
 * (the report's row), came when those ads brought them back: an order from
 * then is 「after」.
 */
export function journeyOf(p: AdPerson, mine: string[] = [], came = NaN): JourneyStep[] {
  const named = new Map<string, string>();
  for (const t of p.taps ?? []) if (t.ad && !named.has(t.ad_id)) named.set(t.ad_id, t.ad);
  const taps = (p.taps ?? []).map((t) => ({ at: when(t.at), adID: t.ad_id, ad: t.ad || adName("", t.ad_id) }));
  // A server from before the road: the one tap it knew.
  if (taps.length === 0 && p.ad_id && !Number.isNaN(when(p.tapped_at))) {
    taps.push({ at: when(p.tapped_at), adID: p.ad_id, ad: named.get(p.ad_id) || p.headline || adName("", p.ad_id) });
  }
  const buys = (p.bought ?? p.orders ?? []).map((o) => ({ at: when(o.at), total: o.total, adID: o.ad_id ?? "" }));
  const events = [...taps.map((t) => ({ ...t, buy: false, total: 0 })), ...buys.map((b) => ({ ...b, ad: "", buy: true }))]
    .filter((e) => !Number.isNaN(e.at))
    .sort((a, b) => a.at - b.at || Number(a.buy) - Number(b.buy));

  const out: JourneyStep[] = [];
  const first = when(p.first_contact_at);
  // Their first message came with a tap when the first tap is that ad, that day.
  const firstTap = events.find((e) => !e.buy);
  const cameByTap = !!firstTap && !Number.isNaN(first) && Math.abs(firstTap.at - first) < 864e5 && firstTap.adID === (p.first_ad_id ?? "");
  if (cameByTap) {
    out.push({ kind: "came", at: firstTap.at, how: "ad", adID: firstTap.adID, ad: firstTap.ad, mine: mine.includes(firstTap.adID) });
    events.splice(events.indexOf(firstTap), 1);
  } else if (!Number.isNaN(first)) {
    // A colleague's pick or the first line's guess names a campaign, not an ad.
    const how = p.first_ad ? "ad" : cameOf(p.first_origin);
    out.push({ kind: "came", at: first, how, adID: "", ad: p.first_ad ?? "", mine: false });
  }
  for (const e of events) {
    if (e.buy) {
      out.push({
        kind: "buy", at: e.at, total: e.total, adID: e.adID, ad: e.adID ? named.get(e.adID) || "" : "",
        mine: mine.includes(e.adID), after: !Number.isNaN(came) && e.at >= came,
      });
      continue;
    }
    const last = out[out.length - 1];
    if (last && last.kind === "tap" && last.adID === e.adID) {
      last.to = e.at;
      last.times++;
      continue;
    }
    out.push({ kind: "tap", at: e.at, to: e.at, times: 1, ad: e.ad, adID: e.adID, mine: mine.includes(e.adID) });
  }
  return out;
}

/** The colours an ad with no picture is told apart by: one an ad, whichever row it is on. */
const TILE = ["#5b4fbf", "#c2662d", "#2b7a6f", "#a3405f", "#3b6ea5", "#7a6a1f"];
const tileOf = (key: string) => {
  let h = 0;
  for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return TILE[Math.abs(h) % TILE.length]!;
};
/** 「001」 from 「001 ( MayterLynn Video ) Promotion」; else its first two characters. */
const codeOf = (ad: string) => {
  const m = /^\s*(\d{2,3})/.exec(ad);
  if (m) return m[1]!;
  return Array.from(adLabel(ad).replace(/[^\p{L}\p{N}]/gu, "")).slice(0, 2).join("") || "AD";
};

/** An ad as the team knows it: the picture on its card, else a tile with its number. */
function AdTile({ adID, ad, pics }: { adID: string; ad: string; pics: AdPics }) {
  const [broken, setBroken] = useState(false);
  const src = adID ? pics[adID] : "";
  if (src && !broken) {
    return <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
  }
  return (
    <span className="jy-code" style={{ background: tileOf(adID || ad) }}>
      {codeOf(ad)}
    </span>
  );
}

const CAME_ICON: Record<Came, ReactNode> = {
  ad: <MdCampaign />,
  adUnknown: <MdCampaign />,
  self: <MdChatBubbleOutline />,
  messenger: <BsMessenger />,
  web: <MdStorefront />,
  dealer: <MdHandshake />,
  referral: <MdGroups />,
  other: <MdChatBubbleOutline />,
};
const CAME_WORD: Record<Came, string> = {
  ad: "点广告来",
  adUnknown: "点广告来，不知道哪条",
  self: "自己来问",
  messenger: "Messenger 来",
  web: "从网店来",
  dealer: "代理带来",
  referral: "朋友介绍",
  other: "其他",
};

/** What a step says in words: the tooltip in a row, the line in the rail. */
function wordsOf(s: JourneyStep): { title: string; meta: string } {
  if (s.kind === "came") {
    return { title: s.ad ? `第一次来 · ${adLabel(s.ad)}` : `第一次来 · ${CAME_WORD[s.how]}`, meta: dayOf(s.at) };
  }
  if (s.kind === "buy") {
    return { title: `买了 ${rmOf(s.total)}`, meta: `${dayOf(s.at)}${s.ad ? ` · 算在 ${adLabel(s.ad)}` : ""}` };
  }
  const from = dayOf(s.at);
  const to = dayOf(s.to);
  return { title: adLabel(s.ad), meta: `${from === to ? from : `${from}–${to}`} 点了${s.times > 1 ? ` ${s.times} 次` : ""}` };
}

/** Days between two steps, said only when it is a day or more. */
const gapOf = (a: number, b: number) => {
  const d = Math.floor((b - a) / 864e5);
  return d >= 1 ? `${d} 天` : "";
};

/** The node itself: the ad's picture, the first visit's icon, or the bag. */
function Dot({ s, pics }: { s: JourneyStep; pics: AdPics }) {
  if (s.kind === "buy") {
    return (
      <span className="jy-dot bag">
        <MdShoppingBag />
      </span>
    );
  }
  if (s.kind === "came" && !(s.how === "ad" && (s.adID || s.ad))) {
    return <span className={`jy-dot icon h-${s.how}`}>{CAME_ICON[s.how]}</span>;
  }
  return (
    <span className="jy-dot pic">
      <AdTile adID={s.adID} ad={s.ad} pics={pics} />
      {s.kind === "tap" && s.times > 1 && <b className="jy-x">×{s.times}</b>}
      {/* The ad that brought them the first time (prototype JyDot): the 首 the legends name. */}
      {s.kind === "came" && <b className="jy-first">首</b>}
    </span>
  );
}

/**
 * A road. "row" across, for a list of people — a picture a step, the date
 * under it, the days between on the line; "rail" down, for one person — the
 * picture, then the ad's name and when, in words.
 */
export function JourneyRoad({ steps, pics, layout = "row" }: { steps: JourneyStep[]; pics: AdPics; layout?: "row" | "rail" }) {
  return (
    <ol className={`jy-road ${layout}`}>
      {steps.map((s, i) => {
        const w = wordsOf(s);
        const gap = i > 0 ? gapOf(steps[i - 1]!.at, s.at) : "";
        const cls = `jy-step ${s.kind}${s.mine ? " mine" : ""}${s.kind === "buy" && s.after ? " after" : ""}`;
        const style = { "--i": i } as CSSProperties;
        // Down, the wire from the step before grows to this one and says how many days passed (prototype LzJourney).
        if (layout === "rail") {
          return (
            <li key={i} className={cls} style={style}>
              {i > 0 && <span className="jy-wire">{gap && <i>隔 {gap}</i>}</span>}
              <Dot s={s} pics={pics} />
              <span className="jy-words">
                <b>{w.title}</b>
                <small>{w.meta}</small>
              </span>
            </li>
          );
        }
        return (
          <li key={i} className={cls} style={style} title={`${w.title}\n${w.meta}`}>
            {i > 0 && <span className="jy-link">{gap && <i>{gap}</i>}</span>}
            <span className="jy-node">
              <Dot s={s} pics={pics} />
              <span className="jy-cap">
                {s.kind === "buy" ? <b className="jy-rm">{rmOf(s.total)}</b> : <b>{dayOf(s.at)}</b>}
                <small>{s.kind === "came" ? "第一次来" : s.kind === "buy" ? dayOf(s.at) : s.times > 1 ? `点了 ${s.times} 次` : "点了"}</small>
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Which ads they tapped, how many orders and for how much: the card's head. */
function sums(steps: JourneyStep[]) {
  const ads = new Set<string>();
  let orders = 0;
  let spent = 0;
  for (const s of steps) {
    if (s.kind === "tap" || (s.kind === "came" && s.how === "ad" && s.adID)) ads.add(s.adID || s.ad);
    if (s.kind === "buy") {
      orders++;
      spent += s.total;
    }
  }
  return { ads: ads.size, orders, spent };
}

type Loaded = { road: AdPerson; pics: AdPics } | null | "error";

/**
 * One customer's 广告足迹 by their number, as the chat's 详情 shows it. plain: under a heading of
 * its own, so no frame and no title of its own — only the counts.
 */
export default function AdJourneyCard({ waID, plain = false }: { waID: string; plain?: boolean }) {
  const [got, setGot] = useState<Loaded>(null);
  useEffect(() => {
    let live = true;
    setGot(null);
    api.leadRoad(waID).then(
      (r) => live && setGot({ road: r.road, pics: r.pics ?? {} }),
      () => live && setGot("error"),
    );
    return () => {
      live = false;
    };
  }, [waID]);
  return <JourneyCard got={got} plain={plain} />;
}

function JourneyCard({ got, plain }: { got: Loaded; plain: boolean }) {
  if (got === "error") {
    return (
      <section className={`jy-card${plain ? " plain" : ""}`}>
        <p className="jy-note">
          <MdErrorOutline aria-hidden="true" />
          拿不到广告足迹，等一下再打开
        </p>
      </section>
    );
  }
  const steps = got ? journeyOf(got.road) : [];
  const t = sums(steps);
  return (
    <section className={`jy-card${plain ? " plain" : ""}`} aria-busy={!got}>
      <div className="jy-head">
        {!plain && (
          <span className="jy-title">
            <MdRoute aria-hidden="true" />
            广告足迹
          </span>
        )}
        {got && (
          <span className="jy-sums">
            {t.ads > 0 && (
              <span>
                <MdCampaign aria-hidden="true" />
                {t.ads} 条广告
              </span>
            )}
            {t.orders > 0 ? (
              <span className="good">
                <MdShoppingBag aria-hidden="true" />
                {t.orders} 单 · {rmOf(t.spent)}
              </span>
            ) : (
              // Said, not left out (prototype JySums): a road with no bag reads as one not loaded yet.
              <span>
                <MdHourglassEmpty aria-hidden="true" />
                还没买
              </span>
            )}
          </span>
        )}
      </div>
      {!got ? (
        <div className="jy-skel" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
      ) : (
        <>
          <JourneyRoad steps={steps} pics={got.pics} layout="rail" />
          {t.ads === 0 && <p className="jy-note">还没点过广告</p>}
        </>
      )}
    </section>
  );
}

/**
 * The card on its own, from the 客户表's 「+N」 (prototype jyOpenCard): a drawer from the right, the whole screen on a
 * phone — the customer's name, what it is, the road, what its marks mean, and 打开对话 along the bottom. It was a white
 * box in the middle of the screen with only the name over it (QA G3-01). Kit's Drawer: over a customer's page Esc and
 * the phone's back close this one only, and it hides with its page. onOpenChat: absent where the chat is open already.
 */
export function AdJourneySheet({ waID, name, onClose, onOpenChat }: { waID: string; name: string; onClose: () => void; onOpenChat?: () => void }) {
  return (
    <Drawer title={name} sub="广告足迹 · 点过什么广告、在哪条广告下单" label={`${name}的广告足迹`} className="jy-drawer" onClose={onClose}>
      <AdJourneyCard waID={waID} plain />
      <div className="jy-legend jy-legend-card" aria-hidden="true">
        <span>
          <i className="first">首</i>第一次来
        </span>
        <span>
          <MdShoppingBag />
          下单，写着算在哪条广告
        </span>
        <span>线上＝隔几天</span>
      </div>
      {onOpenChat && (
        <div className="jy-open">
          <Btn icon="chat" onClick={onOpenChat}>
            打开对话
          </Btn>
        </div>
      )}
    </Drawer>
  );
}

/** Bought after this ad brought them back, or still waiting: the end of a row. */
export function JourneyOutcome({ spent }: { spent: number }) {
  return spent > 0 ? (
    <span className="jy-out good">
      <MdShoppingBag aria-hidden="true" />
      {rmOf(spent)}
    </span>
  ) : (
    <span className="jy-out">
      <MdHourglassEmpty aria-hidden="true" />
      还没买
    </span>
  );
}
